import { useState } from "react";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import type { AnnieAvatarState } from "../../../hooks/useAnnieAvatar";
import type { AnnieNameDraft } from "./useAnnieNameDraft";
import { AnnieStudioChange, type AnnieStudioDraft } from "./useAnnieStudioDraft";

/** The Studio's user actions and their analytics; Reset of a saved design goes through a confirmation. */
export const useAnnieStudioActions = (avatar: AnnieAvatarState, studio: AnnieStudioDraft, name: AnnieNameDraft, onClose: () => void) => {
	const { track } = useAnalytics();
	const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
	const saved = avatar.settings === undefined ? undefined : avatar.settings.avatar;
	// With no saved design, Save turns the avatar on, unless the user only renamed her (a Reset is not a design pick).
	const pickedDesign = studio.revision > 0 && studio.lastChange !== AnnieStudioChange.Reset;
	const willSaveDesign = saved === null ? pickedDesign || !name.isDirty : studio.isDirty;
	const hasChanges = willSaveDesign || name.isDirty;

	return {
		hasChanges,
		canSave: hasChanges && name.isValid,
		isResetConfirmOpen,
		cancelReset: () => setIsResetConfirmOpen(false),
		showChange: (show: boolean) => {
			avatar.setHidden(!show);
			track("annie_avatar_visibility_changed", { hidden: !show });
		},
		surprise: () => {
			studio.randomize();
			track("annie_avatar_randomized", { kind: studio.draft.kind });
		},
		reset: () => (saved === null ? studio.resetDraft() : setIsResetConfirmOpen(true)),
		confirmReset: () => {
			setIsResetConfirmOpen(false);
			avatar.resetAvatar({ onSuccess: studio.resetDraft });
			track("annie_avatar_reset");
		},
		/** One write for the design and the name; an untouched design is sent back as saved. */
		save: () => {
			// Save stays disabled until settings load, so this is a bug, not a state to paper over.
			if (saved === undefined) throw new Error("Annie Studio saved before the avatar settings loaded");
			const event = studio.savedEvent;
			const isRename = name.isDirty;
			const hasCustomName = name.toSave !== null;
			avatar.saveAvatar(
				{ avatar: willSaveDesign ? studio.draft : saved, name: name.toSave },
				{
					onSuccess: () => {
						if (willSaveDesign) track("annie_avatar_saved", { ...event });
						if (isRename) track("annie_avatar_renamed", { has_custom_name: hasCustomName });
						onClose();
					},
				},
			);
		},
	};
};
